import { navigate } from 'app/navigation';
import { apiSlice } from 'api/apiSlice';
import { toCamelCase } from 'utils/utilities';
import { endpoints } from 'helper/endpoints';
import { REAUTHENTICATE_STATUS } from 'features/reauthenticate/helpers';
import {
    updateSelfieCheck,
    updateAdditionalVerification,
    setSessionStatus,
    setSessionUpdateStatus,
    setDeviceInfoSent,
} from 'features/reauthenticate/state';

// All re-auth calls authenticate with the X-Reauthenticate-Session header (NOT the
// Authorization bearer token). httpBaseQuery forwards the per-endpoint `headers`.
const sessionHeader = (sessionId) => ({ 'X-Reauthenticate-Session': sessionId });

const reauthenticateApi = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        verifyReauthenticateSelfie: builder.mutation({
            query: ({ sessionId, payload }) => ({
                url: endpoints.reauthenticateSelfie,
                method: 'POST',
                data: toCamelCase(payload),
                headers: sessionHeader(sessionId),
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                const { data } = await queryFulfilled;

                const selfieVerification = data?.selfieVerification;

                dispatch(updateSelfieCheck(selfieVerification));

                if (selfieVerification?.status === REAUTHENTICATE_STATUS.COMPLETED) {
                    dispatch(updateAdditionalVerification(data.additionalVerification));
                }
            },
        }),

        updateProgress: builder.mutation({
            query: ({ sessionId, status }) => ({
                url: endpoints.reauthenticateProgress,
                method: 'POST',
                data: { status },
                headers: sessionHeader(sessionId),
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                const { data } = await queryFulfilled;
                dispatch(setSessionUpdateStatus(data));
            },
        }),

        getDeviceInfo: builder.query({
            query: (sessionId) => ({
                url: endpoints.reauthenticateDeviceInfo,
                method: 'GET',
                headers: sessionHeader(sessionId),
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                await queryFulfilled;
                dispatch(setDeviceInfoSent());
            },
        }),

        // Fetches the current re-auth session status. Used on screens that mount
        // without the status in state (e.g. desktop, where session storage is used)
        // and to refresh status after a failed code attempt.
        getSessionStatus: builder.query({
            query: (sessionId) => ({
                url: endpoints.reauthenticateStatus,
                method: 'GET',
                headers: sessionHeader(sessionId),
            }),
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setSessionStatus(data));
                } catch {
                    navigate('/session-expired?session=expired', { replace: true });
                }
            },
        }),

        verifyCode: builder.mutation({
            query: ({ sessionId, code }) => ({
                url: endpoints.reauthenticateCode,
                method: 'POST',
                data: { code },
                headers: sessionHeader(sessionId),
            }),
            // Success: persist the verified channel. The error path stays in the
            // component (inline field error + a status refresh for the retry), so the
            // rejection MUST be caught here — otherwise an invalid/expired code
            // (4xx) becomes an unhandled promise rejection and trips the runtime
            // error overlay, hiding the inline field error.
            onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(updateAdditionalVerification(data.additionalVerification));
                } catch {
                    // Failure (invalid/expired code) is surfaced by the component.
                }
            },
        }),

        resendCode: builder.query({
            query: (sessionId) => ({
                url: endpoints.reauthenticateCode,
                method: 'GET',
                headers: sessionHeader(sessionId),
            }),
        }),

        // Passive liveness check for the re-auth selfie; returns the raw result body.
        reauthenticateLivenessCheck: builder.mutation({
            query: ({ sessionId, image }) => ({
                url: endpoints.reauthenticateLiveness,
                method: 'POST',
                data: { selfieImage: image },
                headers: sessionHeader(sessionId),
            }),
        }),

        // AWS liveness. `serverDate` lets the detector correct device clock skew.
        createReauthLivenessSession: builder.mutation({
            query: ({ sessionId }) => ({
                url: endpoints.reauthenticateLivenessSession,
                method: 'POST',
                headers: sessionHeader(sessionId),
            }),
            transformResponse: (data, meta) => ({
                ...data,
                serverDate: meta?.headers?.date ?? null,
            }),
        }),

        // Spends a selfie attempt: call once per liveness session.
        verifyReauthLivenessSelfie: builder.mutation({
            query: ({ sessionId, livenessSessionId }) => ({
                url: endpoints.reauthenticateSelfieVerify,
                method: 'POST',
                data: { sessionId: livenessSessionId },
                headers: sessionHeader(sessionId),
            }),
        }),
    }),
    overrideExisting: false,
});

export const {
    useVerifyReauthenticateSelfieMutation,
    useUpdateProgressMutation,
    useLazyGetDeviceInfoQuery,
    useLazyGetSessionStatusQuery,
    useVerifyCodeMutation,
    useLazyResendCodeQuery,
    useReauthenticateLivenessCheckMutation,
    useCreateReauthLivenessSessionMutation,
    useVerifyReauthLivenessSelfieMutation,
} = reauthenticateApi;
